/**
 * PRISM Phase C — posthog-js instrumentation
 *
 * C1: Hardened PostHog init (autocapture OFF, replay OFF, masking ON,
 *     opt-out-by-default, person_profiles: identified_only).
 * C2: Consent gate — reads the `.flowrent.io` parent-domain cookie written by
 *     the marketing site's vanilla-cookieconsent banner.
 * C3: web-vitals → PostHog (consent-gated).
 * C4: $pageview, feature_used, onboarding_step, UTM + _gl attribution.
 * EP-160: account_id + account_kind super properties and the `account` group;
 *     masked session replay on REPLAY_ROUTES for customer accounts; no client
 *     capture at all in a support session tab (disableAnalyticsForPage).
 *
 * Identity: SHA-256(account_id:user_id) hex — matches the Rails server-side
 * hashing in api/app/services/product_analytics.rb so client + server events
 * stitch to the same PostHog person profile.
 *
 * PII guarantee: never pass email, phone, or any plain-text personal identifier.
 * The only string sent to PostHog as `distinct_id` is the hex digest above.
 */

import posthog from 'posthog-js'

// ---------------------------------------------------------------------------
// Env
// ---------------------------------------------------------------------------
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST as string | undefined

// ---------------------------------------------------------------------------
// C2 — Consent cookie contract (cross-team dependency)
//
// The marketing site (flowrent.io) MUST write this cookie scoped to the
// PARENT domain:
//   Set-Cookie: ...; Domain=.flowrent.io; SameSite=Lax; Secure
//
// If the cookie is host-only (Domain=flowrent.io or Domain=www.flowrent.io)
// app.flowrent.io cannot read it and capturing will never opt-in.
//
// Cookie name  : "fr_consent"
// Granted value: "analytics=true" (substring match — other categories may follow)
//
// Marketing team checklist:
//   - vanilla-cookieconsent config must set `cookie_domain: '.flowrent.io'`
//   - On analytics consent granted: document.cookie = "fr_consent=analytics=true; Domain=.flowrent.io; ..."
//   - On revoke: set "fr_consent=analytics=false" or delete the cookie
// ---------------------------------------------------------------------------
export const CONSENT_COOKIE_NAME = 'fr_consent'
const CONSENT_GRANTED_SUBSTRING = 'analytics=true'

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Read a cookie value by name from `document.cookie`. Returns null if absent. */
function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const prefix = name + '='
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim()
    if (trimmed.startsWith(prefix)) {
      return decodeURIComponent(trimmed.slice(prefix.length))
    }
  }
  return null
}

/** Returns true when the consent cookie signals analytics permission. */
export function hasAnalyticsConsent(): boolean {
  const value = readCookie(CONSENT_COOKIE_NAME)
  if (!value) return false
  return value.includes(CONSENT_GRANTED_SUBSTRING)
}

/**
 * SHA-256 hex of a UTF-8 string (Web Crypto API). Matches the Ruby
 * `Digest::SHA256.hexdigest` used server-side so client + server events share the
 * same distinct_id in PostHog.
 */
async function sha256Hex(input: string): Promise<string> {
  const buffer = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input))
  return Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

/**
 * Production distinct_id: SHA-256("<account_id>:<user_id>") (e.g. "42:7"). Matches
 * the Rails server hash so client + server events stitch to the same person.
 */
export async function hashIdentity(accountId: number, userId: number): Promise<string> {
  return sha256Hex(`${accountId}:${userId}`)
}

/**
 * One self-hosted PostHog is production. Non-prod is the SAME instance — segregated
 * by an `environment` super-property + a single fixed "dev user" distinct_id (the
 * SHA-256 of DEV_DISTINCT_ID_INPUT, matching Rails ProductAnalytics::DEV_DISTINCT_ID),
 * so local traffic collapses to one person and never fragments real prod profiles.
 * `import.meta.env.PROD` is true only in a production build.
 */
const ENVIRONMENT: 'production' | 'development' = import.meta.env.PROD ? 'production' : 'development'
const DEV_DISTINCT_ID_INPUT = 'flowrent:dev'

// ---------------------------------------------------------------------------
// Property sanitizer — the e-sign route (/sign/:token) carries a
// bearer-equivalent token in the URL. PostHog records URLs at the network
// layer ($current_url, $pathname, $referrer, …) — mask_all_text /
// mask_all_element_attributes do NOT redact them. Strip the token from every
// string property before an event leaves the browser (inspection WEB-H3).
// ---------------------------------------------------------------------------
const SIGN_TOKEN_PATTERN = /(\/sign\/)[^/?#\s]+/g
// EP-160: the support console's one-time code (`#support_session=<code>`).
// main.tsx strips it before init; this is the second guard.
const SUPPORT_SESSION_PATTERN = /(support_session=)[^&#\s]+/g

/** Redact e-sign tokens and support session codes from every string property value. Exported for tests. */
export function sanitizeProperties(
  properties: Record<string, unknown>,
): Record<string, unknown> {
  for (const [key, value] of Object.entries(properties)) {
    if (typeof value !== 'string') continue
    let clean = value
    if (clean.includes('/sign/')) clean = clean.replace(SIGN_TOKEN_PATTERN, '$1[redacted]')
    if (clean.includes('support_session=')) clean = clean.replace(SUPPORT_SESSION_PATTERN, '$1[redacted]')
    if (clean !== value) properties[key] = clean
  }
  return properties
}

// ---------------------------------------------------------------------------
// C1 — Init (called once from main.tsx, before the app renders)
// ---------------------------------------------------------------------------
let _initialized = false
// EP-160 section 2.3: set in a support session tab; no client capture for the
// rest of the page's life (no init, no opt-in, no replay, no event helpers).
let _pageOptOut = false

/**
 * Turn client analytics off for this page. main.tsx calls it before
 * initAnalytics in a support tab, and AuthProvider when GET /api/session
 * carries `support_session`. There is no way back short of a new page.
 */
export function disableAnalyticsForPage(): void {
  _pageOptOut = true
  _accountKind = null
  if (_initialized) posthog.opt_out_capturing()
  syncSessionReplay()
}

/** True once disableAnalyticsForPage ran. */
export function isAnalyticsDisabledForPage(): boolean {
  return _pageOptOut
}

export function initAnalytics(): void {
  if (_initialized || _pageOptOut) return
  if (!POSTHOG_KEY || !POSTHOG_HOST) {
    // No-op in environments without keys (e.g. unit-test environment, CI without
    // VITE_POSTHOG_KEY set). All posthog.* calls are safe even before init because
    // posthog-js queues them, but we skip init entirely to avoid network noise.
    return
  }

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,

    // --- capture settings (MASKED posture — logged-in tenant PII app) ---
    // autocapture (clicks/changes/submits) + pageviews are ON, with ALL text +
    // attributes masked so events never carry readable PII. Session REPLAY is
    // OFF: on this host its continuous DOM recording drove the PostHog worker to
    // ~12 GB and OOM-killed other processes. Re-enable only on a host with
    // headroom (Phase P), ideally with sampling. For sensitive elements, add the
    // `ph-no-capture` class to opt them out of autocapture entirely.
    autocapture: true,
    capture_pageview: true,    // initial + SPA history-change pageviews (no manual firing)
    capture_pageleave: true,
    // EP-160 D7: replay never starts on its own. syncSessionReplay starts it
    // only on REPLAY_ROUTES, for a customer account, with consent, and only in
    // a build with VITE_POSTHOG_REPLAY=true; everything in a recording is masked.
    disable_session_recording: true,
    session_recording: { maskAllInputs: true, maskTextSelector: '*' },
    mask_all_text: true,
    mask_all_element_attributes: true,
    person_profiles: 'identified_only',

    // WEB-H3: strip e-sign tokens from URL-bearing properties on every event.
    sanitize_properties: sanitizeProperties,

    // Start opted-out; C2 opts in only after reading the consent cookie.
    opt_out_capturing_by_default: true,

    loaded: (ph) => {
      ph.set_config({ persistence: 'memory' })
      // Tag EVERY event with the environment (super-property) so non-prod data is
      // filterable/excludable inside the single shared PostHog instance.
      ph.register({ environment: ENVIRONMENT })
      // Expose the initialized instance as window.posthog. posthog-js does not
      // attach the global automatically when loaded as an ES module, so without
      // this it's undefined (breaks console debugging + any window.posthog use).
      if (typeof window !== 'undefined') {
        ;(window as unknown as { posthog: typeof ph }).posthog = ph
      }
      // Apply consent state immediately after init.
      applyConsent()
    },
  })

  _initialized = true
}

// ---------------------------------------------------------------------------
// C2 — Consent gate
// ---------------------------------------------------------------------------

/** Opt in or out based on the current cookie state. Call on init and on any
 *  consent-change event (e.g. the marketing banner's onChange callback). */
export function applyConsent(): void {
  if (!_pageOptOut && hasAnalyticsConsent()) {
    posthog.opt_in_capturing()
  } else {
    posthog.opt_out_capturing()
  }
  syncSessionReplay()
}

// ---------------------------------------------------------------------------
// Dev helper — lets engineers toggle the consent cookie on localhost so the
// consent gate is testable without the marketing site running.
//
// Usage in browser console:
//   import('/src/lib/analytics').then(m => m.devSetConsent(true))
// or for the test helper:
//   setAnalyticsConsentCookie(true)
// ---------------------------------------------------------------------------
export function devSetConsent(granted: boolean): void {
  // Dev affordance only — no-op in production builds.
  if (import.meta.env.PROD) return
  if (granted) {
    document.cookie = `${CONSENT_COOKIE_NAME}=${CONSENT_GRANTED_SUBSTRING}; path=/; SameSite=Lax`
  } else {
    document.cookie = `${CONSENT_COOKIE_NAME}=analytics=false; path=/; SameSite=Lax`
  }
  applyConsent()
}

// ---------------------------------------------------------------------------
// Identity (C1 + C4)
// ---------------------------------------------------------------------------

/**
 * Identify the current user in PostHog using a hashed id.
 * Call after a successful login once both account_id and user_id are known.
 *
 * EP-160: also registers `account_id` and `account_kind` on every later event
 * and puts the person in the `account` group with its kind. All three are C0
 * (operational) data; nothing else about the account is sent.
 *
 * @param accountId   - numeric account id from the API
 * @param userId      - numeric user id from the API
 * @param accountKind - `account.kind` from GET /api/session (customer, test, internal, demo)
 * @returns the distinct_id sent to PostHog, so Sentry can name the same person
 */
export async function identifyUser(
  accountId: number,
  userId: number,
  accountKind?: string | null,
): Promise<string> {
  // Prod: real per-user hash (stitches with the Rails server hash). Non-prod: a
  // single fixed "dev user" for ALL accounts, so dev traffic collapses to one person
  // and never pollutes / fragments real production profiles.
  const id =
    ENVIRONMENT === 'production'
      ? await hashIdentity(accountId, userId)
      : await sha256Hex(DEV_DISTINCT_ID_INPUT)
  if (_pageOptOut) return id
  posthog.identify(id)
  posthog.register(accountKind ? { account_id: accountId, account_kind: accountKind } : { account_id: accountId })
  posthog.group('account', String(accountId), accountKind ? { kind: accountKind } : undefined)
  _accountKind = accountKind ?? null
  syncSessionReplay()
  return id
}

/**
 * Reset PostHog identity on logout. Clears the current person profile and
 * begins a new anonymous session.
 */
export function resetIdentity(): void {
  _accountKind = null
  syncSessionReplay()
  posthog.reset()
}

// ---------------------------------------------------------------------------
// EP-160 D7: masked session replay
// ---------------------------------------------------------------------------

/** The only routes ever recorded: the onboarding wizard and a building's own pages. */
export const REPLAY_ROUTES: readonly RegExp[] = [/^\/onboarding(\/|$)/, /^\/properties\/[^/]+/]

/** True when the path is one of REPLAY_ROUTES. */
export function isReplayRoute(pathname: string): boolean {
  return REPLAY_ROUTES.some((route) => route.test(pathname))
}

// Build switch, off unless set to "true", so a build can ship without replay
// until the App Store and Play privacy answers cover it. Read on every call so
// the value comes from the build, not from the module's load order.
function replayBuildEnabled(): boolean {
  return import.meta.env.VITE_POSTHOG_REPLAY === 'true'
}

let _accountKind: string | null = null
let _replayPath: string | null = null
let _replayOn = false

/**
 * Start or stop the recording for the current state. Call with the new path on
 * every route change; identifyUser, resetIdentity and applyConsent call it
 * without one. Records only when PostHog is initialised and all four hold:
 * the build switch is on, the person consented to analytics, the account is a
 * customer account (not test, internal or demo) and the path is one of
 * REPLAY_ROUTES.
 */
export function syncSessionReplay(pathname?: string): void {
  if (pathname !== undefined) _replayPath = pathname
  const shouldRecord =
    _initialized &&
    !_pageOptOut &&
    replayBuildEnabled() &&
    hasAnalyticsConsent() &&
    _accountKind === 'customer' &&
    _replayPath !== null &&
    isReplayRoute(_replayPath)
  if (shouldRecord === _replayOn) return
  _replayOn = shouldRecord
  if (shouldRecord) {
    posthog.startSessionRecording()
  } else {
    posthog.stopSessionRecording()
  }
}

// ---------------------------------------------------------------------------
// Typed event helper (C1 — expose a typed `track()` wrapper)
// ---------------------------------------------------------------------------

/** Union of all event names sent by the client. */
export type AnalyticsEvent =
  | '$pageview'
  | 'feature_used'
  | 'onboarding_step'
  | 'web_vital'
  | 'report_viewed'
  | 'report_exported'
  | 'coach_clicked'

export interface PageviewProps {
  path?: string
  title?: string
}

export interface FeatureUsedProps {
  feature: string
  [key: string]: string | number | boolean | undefined
}

export interface OnboardingStepProps {
  step: number
  wizard?: string
}

export interface WebVitalProps {
  name: string
  value: number
  rating: 'good' | 'needs-improvement' | 'poor'
  delta: number
  id: string
}

// EP-73 — coach signal events.
export interface ReportEventProps {
  report: string
}

export interface CoachClickedProps {
  feature_id: string
  kind: 'resume' | 'discover'
}

type EventPropsMap = {
  $pageview: PageviewProps
  feature_used: FeatureUsedProps
  onboarding_step: OnboardingStepProps
  web_vital: WebVitalProps
  report_viewed: ReportEventProps
  report_exported: ReportEventProps
  coach_clicked: CoachClickedProps
}

/**
 * Typed wrapper around posthog.capture.
 * All events are consent-gated by posthog-js itself (opt_out_capturing_by_default).
 */
export function track<E extends AnalyticsEvent>(
  event: E,
  props: EventPropsMap[E],
): void {
  if (_pageOptOut) return
  posthog.capture(event, props)
}

// EP-160 section 2.6: the wizard was left before its last step (on pagehide).
export interface OnboardingAbandonedProps {
  step: number
  seconds_on_step: number
}

/**
 * Sent as the page goes away, so it skips the batch queue and goes by
 * sendBeacon, which the browser delivers after the page has unloaded.
 * Consent-gated like every event (posthog-js drops it while opted out).
 */
export function trackOnboardingAbandoned(props: OnboardingAbandonedProps): void {
  if (_pageOptOut) return
  posthog.capture('onboarding_abandoned', { ...props }, { transport: 'sendBeacon', send_instantly: true })
}

// ---------------------------------------------------------------------------
// EP-73 — Tier-1 form-lifecycle telemetry
// ---------------------------------------------------------------------------

/** Forms instrumented for abandonment detection (the coach's Tier 1). */
export const TELEMETRY_FORMS = ['invoice', 'lease', 'property', 'expense', 'maintenance'] as const
export type TelemetryForm = (typeof TELEMETRY_FORMS)[number]

/**
 * Emits the name-embedded form lifecycle events the coach's EventProfile
 * matches on (`form_started:invoice` PRESENT + `form_submitted:invoice`
 * ABSENT = abandoned). The form name rides in the EVENT NAME by contract —
 * a property would require the HogQL profile query to join on properties.
 * No field values are ever captured.
 */
export function trackFormLifecycle(
  phase: 'started' | 'submitted',
  form: TelemetryForm,
): void {
  if (_pageOptOut) return
  posthog.capture(`form_${phase}:${form}`)
}

// ---------------------------------------------------------------------------
// C4 — Attribution: capture UTM params + GA4 _gl linker on first landing
// ---------------------------------------------------------------------------

/** Extract UTM params and the GA4 cross-domain linker from the current URL. */
export function extractAttribution(): Record<string, string> {
  if (typeof window === 'undefined') return {}
  const params = new URLSearchParams(window.location.search)
  const attrs: Record<string, string> = {}
  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']
  for (const key of utmKeys) {
    const val = params.get(key)
    if (val) attrs[key] = val
  }
  // Preserve the GA4 cross-domain linker parameter so the marketing→app funnel
  // can be attributed without running GA4 in the app.
  const gl = params.get('_gl')
  if (gl) attrs['_gl'] = gl
  return attrs
}

/**
 * Capture UTM + _gl attribution as person properties on first app landing.
 * Should be called once (from main.tsx). Consent-gated by posthog-js internally.
 *
 * NOTE: does NOT fire a $pageview — posthog-js captures pageviews itself
 * (capture_pageview: true, incl. SPA history changes), so firing one here would
 * double-count. Attribution is set at the person level and attaches to the
 * person's events.
 */
export function captureAttribution(): void {
  if (_pageOptOut) return
  const attrs = extractAttribution()
  if (Object.keys(attrs).length > 0) {
    posthog.setPersonProperties(attrs)
  }
}

// ---------------------------------------------------------------------------
// C3 — web-vitals → PostHog
// ---------------------------------------------------------------------------

/**
 * Register web-vitals collectors. Call once after the app has mounted.
 * Each metric is sent as a `web_vital` event (consent-gated by posthog-js).
 *
 * Bundle impact: web-vitals is tree-shaken; the five collectors (CLS, LCP,
 * INP, FCP, TTFB) add approximately 1.5 kB gzipped to the bundle.
 */
export async function registerWebVitals(): Promise<void> {
  if (_pageOptOut) return
  // Dynamic import so the module is loaded only after the app is interactive,
  // keeping the critical-path bundle unaffected.
  const { onCLS, onLCP, onINP, onFCP, onTTFB } = await import('web-vitals')

  const reportVital = (metric: {
    name: string
    value: number
    rating: 'good' | 'needs-improvement' | 'poor'
    delta: number
    id: string
  }) => {
    track('web_vital', {
      name: metric.name,
      value: metric.value,
      rating: metric.rating,
      delta: metric.delta,
      id: metric.id,
    })
  }

  onCLS(reportVital)
  onLCP(reportVital)
  onINP(reportVital)
  onFCP(reportVital)
  onTTFB(reportVital)
}
